import React, { useEffect, useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, Legend,
} from 'recharts';
import {
    Box, Grid, Card, CardContent, Typography, Chip, Stack,
    Button, LinearProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Skeleton, Fade,
} from '@mui/material';
import {
    TrendingUp, DollarSign, Users, FolderOpen, Receipt,
    CreditCard, Clock, AlertTriangle, CheckCircle2, BarChart3, Target,
    ArrowUpRight, ArrowDownRight, Activity, MapPin, Plane, Hotel, Shield,
    Car, Compass, FileText, UserCheck, Percent, Inbox,
    Banknote,
} from 'lucide-react';
import apiClient from '../../../services/api-client';
import { KpiCard } from '../../../components/charts/kpi-card';
import DonutChartCard from '../../../components/charts/charts';

// ── Palette tokens ────────────────────────────────────────────────────────────
const C = {
    bg: '#faf7f2',
    navy: '#1a1a2e',
    navyDark: '#2a2a4e',
    gold: '#c9a84c',
    goldDark: '#b08d3a',
    border: 'rgba(201,168,76,0.12)',
    borderLight: 'rgba(201,168,76,0.08)',
    muted: '#888',
    faint: '#999',
    dimmer: '#aaa',
    pale: '#f0ebe0',
    white60: 'rgba(255,255,255,0.6)',
};

const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";

const PRESTATION_COLORS = ['#c9a84c', '#1a1a2e', '#2e7d32', '#ef6c00', '#6a1b9a', '#78909c'];

// ── Label maps ────────────────────────────────────────────────────────────────
const statusLabels = {
    'CONFIRMÉ': 'Confirmé', 'BROUILLON': 'Brouillon', 'TERMINE': 'Terminé',
    'ANNULÉ': 'Annulé', 'PAYÉE': 'Payée', 'ÉMISE': 'Émise',
    'PARTIELLEMENT_PAYÉE': 'Partielle', 'EN_RETARD': 'En retard', 'ANNULÉE': 'Annulée',
};

const methodLabels = {
    'VIREMENT': 'Virement', 'CARTE': 'Carte bancaire',
    'ESPÈCES': 'Espèces', 'CHÈQUE': 'Chèque',
};

const prestationLabels = {
    'HOTEL': 'Hôtels', 'BILLET_AVION': 'Billets avion', 'ASSURANCE': 'Assurances',
    'TRANSFERT': 'Transferts', 'EXCURSION': 'Excursions', 'VISA': 'Visas',
};

const prestationIcons = {
    'HOTEL': <Hotel size={16} />, 'BILLET_AVION': <Plane size={16} />,
    'ASSURANCE': <Shield size={16} />, 'TRANSFERT': <Car size={16} />,
    'EXCURSION': <Compass size={16} />, 'VISA': <FileText size={16} />,
};

const activityTypeConfig = {
    'PAYMENT_RECEIVED': { icon: <DollarSign size={16} />, color: '#2e7d32', label: 'Paiement' },
    'INVOICE_CREATED': { icon: <Receipt size={16} />, color: C.gold, label: 'Facture' },
    'DOSSIER_CONFIRMED': { icon: <FolderOpen size={16} />, color: '#1565c0', label: 'Dossier' },
    'INVOICE_OVERDUE': { icon: <AlertTriangle size={16} />, color: '#ef6c00', label: 'Retard' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function safeDate(dateStr, opts = {}) {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('fr-FR', opts);
}

function getInitials(name) {
    if (!name || typeof name !== 'string') return '?';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// ── Shared card style ─────────────────────────────────────────────────────────
const cardSx = {
    bgcolor: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    boxShadow: 'none',
    overflow: 'hidden',
    transition: 'box-shadow .2s',
    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.05)' },
};

// ── Recharts custom tooltip ───────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: C.navy,
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            minWidth: 140,
        }}>
            {label && (
                <span style={{
                    color: C.gold,
                    fontWeight: 600,
                    fontSize: 11,
                    display: 'block',
                    marginBottom: 4,
                    fontFamily: sans,
                }}>
                    {label}
                </span>
            )}
            {payload.map((item, i) => (
                <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    color: C.bg,
                    fontFamily: sans,
                    marginTop: i > 0 ? 2 : 0,
                }}>
                    <span style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: item.color,
                        flexShrink: 0,
                    }} />
                    <span>
                        {item.name}: <strong>{typeof item.value === 'number' ? item.value.toLocaleString('fr-FR') : item.value}</strong>
                    </span>
                </div>
            ))}
        </div>
    );
}

// ── Empty State ───────────────────────────────────────────────────────────────
function EmptyState({ icon, title, subtitle }) {
    return (
        <Box py={6} textAlign="center">
            <Box sx={{ color: C.dimmer, mb: 1.5, opacity: 0.5 }}>{icon}</Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.navy, mb: 0.5, fontFamily: sans }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.dimmer, fontFamily: sans }}>{subtitle}</Typography>
        </Box>
    );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function ReportKPI({ title, value, subtitle, icon, color, bgColor, trend, trendValue }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card sx={{ ...cardSx, border: `1px solid ${bgColor}` }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 2, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: bgColor, color,
                        }}>
                            {icon}
                        </Box>
                        {trend && trendValue && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                fontSize: 12, fontWeight: 600, px: 1, py: 0.25, borderRadius: 4,
                                color: trend === 'up' ? '#2e7d32' : '#c62828',
                                bgcolor: trend === 'up' ? 'rgba(46,125,50,0.08)' : 'rgba(198,40,40,0.08)',
                            }}>
                                {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'inherit' }}>{trendValue}</Typography>
                            </Box>
                        )}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: '#666', fontFamily: sans, mb: 0.5 }}>{title}</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 700, color: C.navy, fontFamily: serif, lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: C.faint, fontFamily: sans, mt: 0.5 }}>{subtitle}</Typography>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ── Section Card ──────────────────────────────────────────────────────────────
function SectionCard({ title, icon, children }) {
    return (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Card sx={cardSx}>
                <Box sx={{
                    px: 2.5, py: 2, borderBottom: `1px solid ${C.borderLight}`,
                    bgcolor: 'rgba(201,168,76,0.02)',
                    display: 'flex', alignItems: 'center', gap: 1,
                }}>
                    <Box sx={{ color: C.gold, display: 'flex' }}>{icon}</Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>{children}</Box>
            </Card>
        </motion.div>
    );
}

// ── Mini pie legend row ───────────────────────────────────────────────────────
function PieLegendRow({ items = [], getLabel }) {
    const total = items.reduce((s, d) => s + (d.count || 0), 0);
    return (
        <Stack spacing={1}>
            {items.map((item, idx) => {
                const count = item.count || 0;
                const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
                const label = getLabel ? getLabel(item) : (item.status || item.method || `Item ${idx}`);
                return (
                    <Box key={`${label}-${idx}`}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color || '#ccc', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, color: C.navy, fontFamily: sans, noWrap: true }}>{label}</Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, ml: 1, fontFamily: sans }}>{count}</Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate" value={Math.min(parseFloat(pct), 100)}
                            sx={{ height: 4, borderRadius: 2, bgcolor: C.pale, '& .MuiLinearProgress-bar': { bgcolor: item.color || '#ccc' } }}
                        />
                    </Box>
                );
            })}
        </Stack>
    );
}

// ── Skeleton Loader ──────────────────────────────────────────────────────────
function SkeletonLoader() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={12} sm={6} lg={3} key={i}>
                        <Skeleton variant="rounded" height={128} sx={{ bgcolor: C.pale, borderRadius: 3 }} />
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={3}>
                {[1, 2].map((i) => (
                    <Grid item xs={12} xl={6} key={i}>
                        <Skeleton variant="rounded" height={320} sx={{ bgcolor: C.pale, borderRadius: 3 }} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

// ── Main Reporting Module ─────────────────────────────────────────────────────
export function ReportingModule() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');

    const gradId1 = useId();
    const gradId2 = useId();
    const revenueGrad = `revenueGrad-${gradId1}`;
    const profitGrad = `profitGrad-${gradId2}`;

    const fetchData = async () => {
        setLoading(true);
        setApiError(false);
        try {
            const response = await apiClient.get('/api/reports/dashboard');
            setData(response.data || {});
        } catch (error) {
            console.error("Erreur de récupération du reporting:", error);
            setApiError(true);
            setData({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (apiError) {
        return (
            <Box p={5} textAlign="center">
                <AlertTriangle size={48} color={C.gold} style={{ margin: '0 auto 16px' }} />
                <Typography variant="h5" sx={{ color: C.navy, fontFamily: serif, mb: 2, fontWeight: 700 }}>
                    Erreur de chargement
                </Typography>
                <Typography sx={{ color: C.muted, mb: 3, fontFamily: sans }}>
                    Impossible de récupérer les données du tableau de bord.
                </Typography>
                <Button
                    variant="outlined"
                    onClick={fetchData}
                    sx={{
                        borderColor: C.gold,
                        color: C.navy,
                        textTransform: 'none',
                        fontFamily: sans,
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': { borderColor: C.goldDark, bgcolor: 'rgba(201,168,76,0.08)' },
                    }}
                >
                    Réessayer
                </Button>
            </Box>
        );
    }

    if (loading || !data || Object.keys(data).length === 0) {
        return <SkeletonLoader />;
    }


    const kpi = data?.financeMetrics || {};

    const MONTH_NAMES = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const monthlyRevenueData = Array.isArray(data?.monthlyTrends)
        ? data.monthlyTrends.map(m => ({
            month: MONTH_NAMES[(m.mois ?? 1) - 1],
            revenue: m.chiffreAffaire ?? 0,
            expenses: m.depenses ?? 0,
            profit: (m.chiffreAffaire ?? 0) - (m.depenses ?? 0),
            dossierCount: m.nombreFactures ?? 0,
        }))
        : [];

    const dossierStatusDistributionData = Array.isArray(data?.dossierStats)
        ? data.dossierStats.map((d, i) => ({
            statut: d.statut,
            count: d.count,
            color: ['#1565c0', '#c9a84c', '#2e7d32', '#c62828'][i % 4],
        }))
        : [];

    const invoiceStatusDistributionData = Array.isArray(data?.factureStats)
        ? data.factureStats.map((f, i) => ({
            status: f.statut,
            count: f.count,
            color: ['#2e7d32', '#1565c0', '#ef6c00', '#c62828'][i % 4],
        }))
        : [];

    // Pas de données de paiement par méthode dans ton API — tableau vide
    const paymentMethodDistributionData = [];

    const topClientsData = Array.isArray(data?.clientsPerformance?.content)
        ? data.clientsPerformance.content.map(c => ({
            name: c.clientName,
            revenue: c.chiffreAffaire ?? 0,
            dossierCount: c.totalDossiers ?? 0,
            lastActivity: null,
        }))
        : [];

    const agentPerformanceData = Array.isArray(data?.topAgents)
        ? data.topAgents.map(a => ({
            name: a.agentUsername,
            role: 'Agent',
            dossierCount: a.dossiersConfirmes ?? 0,
            totalRevenue: a.volumeVentes ?? 0,
            avgDossierValue: a.dossiersConfirmes > 0
                ? Math.round(a.volumeVentes / a.dossiersConfirmes)
                : 0,
            paidInvoices: 0,
            pendingInvoices: 0,
            conversionRate: 0,
        }))
        : [];

    const prestationTypeBreakdownData = Array.isArray(data?.productMargins)
        ? data.productMargins.map(p => ({
            type: p.categorieProduit,
            revenue: p.chiffreAffaires ?? 0,
            count: p.volumeVendu ?? 0,
            percentage: kpi.chiffreAffairesTotal > 0
                ? +((p.chiffreAffaires / kpi.chiffreAffairesTotal) * 100).toFixed(1)
                : 0,
        }))
        : [];

    const sections = [
        { id: 'overview', label: "Vue d'ensemble", icon: <BarChart3 size={16} /> },
        { id: 'financial', label: 'Analyse Financière', icon: <DollarSign size={16} /> },
        { id: 'activity', label: 'Activité', icon: <Activity size={16} /> },
        { id: 'clients', label: 'Clients & Agents', icon: <Users size={16} /> },
    ];

    const tabBtnSx = (id) => ({
        textTransform: 'none',
        fontSize: 14,
        fontFamily: sans,
        fontWeight: 600,
        borderRadius: 0.5,
        px: 2,
        py: 0.75,
        bgcolor: activeSection === id ? 'rgba(201,168,76,0.15)' : 'transparent',
        color: activeSection === id ? C.gold : C.muted,
        border: `1px solid ${activeSection === id ? 'rgba(201,168,76,0.3)' : 'transparent'}`,
        '&:hover': { bgcolor: 'rgba(201,168,76,0.08)', color: C.gold },
        transition: 'all 0.2s ease',
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── Section Navigation ── */}
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, role: 'tablist', ariaLabel: "Sections du reporting" }}>
                {sections.map((s) => (
                    <Button
                        key={s.id}
                        role="tab"
                        aria-selected={activeSection === s.id}
                        aria-controls={`panel-${s.id}`}
                        onClick={() => setActiveSection(s.id)}
                        sx={tabBtnSx(s.id)}
                        startIcon={s.icon}
                    >
                        {s.label}
                    </Button>
                ))}
            </Box>

            <AnimatePresence mode="wait">

                {/* ══════════════════════════════════════════════
                    OVERVIEW
                ══════════════════════════════════════════════ */}
                {activeSection === 'overview' && (
                    <motion.div
                        key="overview"
                        role="tabpanel"
                        id="panel-overview"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                    >
                        {/* KPI Row 1 */}
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Total" value={data?.financeMetrics?.chiffreAffairesTotal ?? 0} subtitle="Chiffre d'Affaires" icon={<Banknote />} color="#28026eff" bgColor="rgba(40, 2, 110,0.1)" delay={0} /></Grid>
                            <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Marge Brute" value={data?.financeMetrics?.chiffreAffairesTotal ?? 0} subtitle="Chiffre d'Affaires" icon={<TrendingUp />} color="#2e7d32" bgColor="rgba(46,125,50,0.08)" delay={0} /></Grid>
                            {data?.dossierStats?.map((stat, index) => (
                                <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Dossier" value={stat.count} subtitle={stat.statut} icon={<FolderOpen />} color={stat.statut === "CONFIRME" ? "#03733fff" : "#823c03ff"} bgColor="rgba(40, 2, 110,0.1)" delay={index * 0.1} /></Grid>
                            ))}
                        </Grid>

                        {/* KPI Row 2 */}
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Total" value={data?.financeMetrics?.totalEncaisse ?? 0} subtitle="Montant Encaissé" icon={<CheckCircle2 />} color="#2e7d32" bgColor="rgba(46,125,50,0.08)" delay={0} /></Grid>
                            <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Total" value={data?.financeMetrics?.chiffreAffairesTotal - data?.financeMetrics?.totalEncaisse ?? 0} subtitle="Reste à Encaisser" icon={<Clock />} color="#ef6c00" bgColor="rgba(239,108,0,0.08)" delay={0} /></Grid>
                        </Grid>

                        {/* Revenue Area + Dossier Status Pie */}
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 3, flexWrap: "wrap" }}>
                            <Box sx={{ width: "60%", flexGrow: 1 }}>
                                <SectionCard title="Évolution du Chiffre d'Affaires" icon={<TrendingUp size={20} />}>
                                    {/* 1. Hauteur responsive via les breakpoints MUI (xs, sm, md) */}
                                    <Box sx={{ width: '100%', height: { xs: 240, sm: 288, md: 320 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart
                                                data={monthlyRevenueData}
                                                // 2. Marges ajustées pour éviter que l'axe Y ou les libellés X ne soient coupés sur mobile
                                                margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                                            >
                                                <defs>
                                                    <linearGradient id={revenueGrad} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id={profitGrad} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.2} />
                                                        <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />

                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 10, fill: C.muted }}
                                                    angle={-30}
                                                    textAnchor="end"
                                                    height={40} // 3. Espace garanti pour les textes inclinés
                                                />

                                                <YAxis
                                                    tick={{ fontSize: 11, fill: C.muted }}
                                                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                                    width={45} // 4. Largeur fixe pour empêcher l'axe Y de "sauter" ou d'être coupé lors du redimensionnement
                                                />

                                                <Tooltip content={<ChartTooltip />} />

                                                {/* 5. Espacement ajouté au-dessus de la légende pour qu'elle ne chevauche pas l'axe X sur mobile */}
                                                <Legend
                                                    iconType="circle"
                                                    wrapperStyle={{ fontSize: 11, fontFamily: sans, paddingTop: '10px' }}
                                                />

                                                <Area type="monotone" dataKey="revenue" name="CA" stroke="#c9a84c" fill={`url(#${revenueGrad})`} strokeWidth={2.5} />
                                                <Area type="monotone" dataKey="profit" name="Profit" stroke="#2e7d32" fill={`url(#${profitGrad})`} strokeWidth={2} strokeDasharray="5 5" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </SectionCard>
                            </Box>
                            <Box sx={{ width: "40%", flexGrow: 1 }}>
                                <DonutChartCard
                                    title="Statut des Dossiers"
                                    data={dossierStatusDistributionData}
                                    colors={['#1565c0', '#c9a84c', '#2e7d32', '#c62828']}
                                />
                            </Box>
                        </Box>

                        {/* Invoice Status + Payment Method */}
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
                            <Box sx={{ width: "50%" }}>
                                <SectionCard title="Distribution des Factures" icon={<Receipt size={20} />}>
                                    {invoiceStatusDistributionData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucune facture" subtitle="Les données de facturation apparaîtront ici" />
                                    ) : (
                                        <Box sx={{ height: 224 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={invoiceStatusDistributionData} margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />
                                                    <XAxis dataKey="status" tick={{ fontSize: 11, fill: C.navy }} tickFormatter={(v) => statusLabels[v] || v} />
                                                    <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                                                    <Tooltip content={<ChartTooltip />} />
                                                    <Bar dataKey="count" name="Factures" radius={[6, 6, 0, 0]} maxBarSize={50}>
                                                        {invoiceStatusDistributionData.map((entry, i) => (
                                                            <Cell key={`bar-${i}`} fill={entry.color || '#ccc'} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </SectionCard>
                            </Box>
                            <Box sx={{ width: "50%" }}>
                                <SectionCard title="Modes de Paiement" icon={<CreditCard size={20} />}>
                                    {paymentMethodDistributionData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucun paiement" subtitle="Les modes de paiement apparaîtront ici" />
                                    ) : (
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box sx={{ width: { xs: '45%', sm: '50%' }, height: 192 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={paymentMethodDistributionData}
                                                            cx="50%" cy="50%"
                                                            innerRadius={35}
                                                            outerRadius={65}
                                                            paddingAngle={3}
                                                            dataKey="count"
                                                            stroke="none"
                                                        >
                                                            {paymentMethodDistributionData.map((entry, i) => (
                                                                <Cell key={`cell-${i}`} fill={entry.color || '#ccc'} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip content={<ChartTooltip />} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack spacing={1.5}>
                                                    {paymentMethodDistributionData.map((item, idx) => {
                                                        const total = paymentMethodDistributionData.reduce((s, d) => s + (d.count || 0), 0);
                                                        const pct = total > 0 ? (((item.count || 0) / total) * 100).toFixed(1) : '0';
                                                        return (
                                                            <Box key={`${item.method}-${idx}`}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                                                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color || '#ccc' }} />
                                                                        <Typography sx={{ fontSize: 12, color: C.navy, fontFamily: sans }}>{methodLabels[item.method] || item.method}</Typography>
                                                                    </Stack>
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, fontFamily: sans }}>
                                                                        {item.count || 0} ({pct}%)
                                                                    </Typography>
                                                                </Stack>
                                                                <LinearProgress variant="determinate" value={Math.min(parseFloat(pct), 100)}
                                                                    sx={{ height: 6, borderRadius: 2, bgcolor: C.pale, '& .MuiLinearProgress-bar': { bgcolor: item.color || '#ccc' } }} />
                                                                <Typography sx={{ fontSize: 10, color: C.faint, mt: 0.25, fontFamily: sans }}>
                                                                    {(item.amount || 0).toLocaleString('fr-FR')} €
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    )}
                                </SectionCard>
                            </Box>
                        </Box>
                    </motion.div>
                )}

                {/* ══════════════════════════════════════════════
                    FINANCIAL
                ══════════════════════════════════════════════ */}
                {activeSection === 'financial' && (
                    <motion.div
                        key="financial"
                        role="tabpanel"
                        id="panel-financial"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                    >
                        {/* Revenue vs Expenses grouped bar */}
                        <SectionCard title="Revenus vs Dépenses (12 derniers mois)" icon={<DollarSign size={20} />}>
                            {monthlyRevenueData.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucune donnée financière" subtitle="Les données mensuelles apparaîtront ici" />
                            ) : (
                                <Box sx={{ height: 320 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={monthlyRevenueData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />
                                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.muted }} angle={-30} textAnchor="end" height={50} />
                                            <YAxis tick={{ fontSize: 11, fill: C.muted }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k €`} />
                                            <Tooltip content={<ChartTooltip />} />
                                            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                            <Bar dataKey="revenue" name="Revenus" fill="#c9a84c" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                            <Bar dataKey="expenses" name="Dépenses" fill="#ef6c00" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                            <Bar dataKey="profit" name="Profit" fill="#2e7d32" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </SectionCard>

                        {/* Prestation horizontal bar + detail table */}
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
                            <Box sx={{ width: '50%' }}>
                                <SectionCard title="Revenus par Type de Prestation (en FCFA)" icon={<BarChart3 size={20} />}>
                                    {prestationTypeBreakdownData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucune prestation" subtitle="Les données de prestations apparaîtront ici" />
                                    ) : (
                                        <Box sx={{ height: 256 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={prestationTypeBreakdownData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" horizontal={false} />
                                                    <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                                    <YAxis type="category" dataKey="type" width={100} tick={{ fontSize: 11, fill: C.navy }}
                                                        tickFormatter={(v) => prestationLabels[v] || v} />
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: C.navy, border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, fontSize: 12 }}
                                                        labelStyle={{ color: C.gold }}
                                                        itemStyle={{ color: C.bg }}
                                                        formatter={(value) => [`${Number(value).toLocaleString('fr-FR')}`, 'Revenu']}
                                                    />
                                                    <Bar dataKey="revenue" name="Revenu" radius={[0, 6, 6, 0]} maxBarSize={28}>
                                                        {prestationTypeBreakdownData.map((_, i) => (
                                                            <Cell key={`cell-${i}`} fill={PRESTATION_COLORS[i % PRESTATION_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </SectionCard>
                            </Box>
                            <Box sx={{ width: '50%' }}>
                                <SectionCard title="Détail par Prestation (en FCFA)" icon={<Receipt size={20} />}>
                                    {prestationTypeBreakdownData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucune prestation" subtitle="Les détails apparaîtront ici" />
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {prestationTypeBreakdownData.map((item, idx) => (
                                                <Box
                                                    key={`${item.type}-${idx}`}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        p: 1.5, borderRadius: 2, border: `1px solid ${C.borderLight}`,
                                                        transition: 'background .15s', '&:hover': { bgcolor: C.white60 },
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                                                        bgcolor: 'rgba(201,168,76,0.08)', color: C.gold,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        {prestationIcons[item.type] || <FileText size={16} />}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy, fontFamily: sans, noWrap: true }}>
                                                                {prestationLabels[item.type] || item.type}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                                                {(item.revenue || 0).toLocaleString('fr-FR')}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.25 }}>
                                                            <Typography sx={{ fontSize: 11, color: C.muted, fontFamily: sans }}>{item.count || 0} prestations</Typography>
                                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.gold, fontFamily: sans }}>{item.percentage || 0}%</Typography>
                                                        </Box>
                                                        <LinearProgress variant="determinate" value={Math.min(item.percentage || 0, 100)}
                                                            sx={{ height: 6, borderRadius: 2, bgcolor: C.pale, mt: 0.5, '& .MuiLinearProgress-bar': { bgcolor: C.gold } }} />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </SectionCard>
                            </Box>
                        </Box>

                        {/* Payment amount by method bar */}
                        <SectionCard title="Montants par Mode de Paiement" icon={<CreditCard size={20} />}>
                            {paymentMethodDistributionData.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucun paiement" subtitle="Les montants par mode de paiement apparaîtront ici" />
                            ) : (
                                <Box sx={{ height: 224 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={paymentMethodDistributionData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />
                                            <XAxis dataKey="method" tick={{ fontSize: 11, fill: C.navy }} tickFormatter={(v) => methodLabels[v] || v} />
                                            <YAxis tick={{ fontSize: 11, fill: C.muted }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k €`} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: C.navy, border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, fontSize: 12 }}
                                                labelStyle={{ color: C.gold }}
                                                itemStyle={{ color: C.bg }}
                                                formatter={(value) => [`${Number(value).toLocaleString('fr-FR')} €`, 'Montant']}
                                            />
                                            <Bar dataKey="amount" name="Montant" radius={[6, 6, 0, 0]} maxBarSize={50}>
                                                {paymentMethodDistributionData.map((entry, i) => (
                                                    <Cell key={`cell-${i}`} fill={entry.color || '#ccc'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </SectionCard>
                    </motion.div>
                )}

                {/* ══════════════════════════════════════════════
                    ACTIVITY
                ══════════════════════════════════════════════ */}
                {activeSection === 'activity' && (
                    <motion.div
                        key="activity"
                        role="tabpanel"
                        id="panel-activity"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                    >
                        {/* Weekly Activity bar */}
                        {/* <SectionCard title="Activité Hebdomadaire" icon={<Activity size={20} />}>
                            {weeklyActivityData?.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucune activité" subtitle="Les données hebdomadaires apparaîtront ici" />
                            ) : (
                                <Box sx={{ height: 288 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={weeklyActivityData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />
                                            <XAxis dataKey="week" tick={{ fontSize: 11, fill: C.navy }} />
                                            <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                                            <Tooltip content={<ChartTooltip />} />
                                            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                            <Bar dataKey="dossiers" name="Dossiers" fill="#c9a84c" radius={[4, 4, 0, 0]} maxBarSize={25} />
                                            <Bar dataKey="payments" name="Paiements" fill="#2e7d32" radius={[4, 4, 0, 0]} maxBarSize={25} />
                                            <Bar dataKey="invoices" name="Factures" fill="#6a1b9a" radius={[4, 4, 0, 0]} maxBarSize={25} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </SectionCard> */}

                        {/* Destinations + Dossier line trend */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} xl={6}>
                                <SectionCard title="Destinations Populaires" icon={<MapPin size={20} />}>
                                    {/* {destinationPopularityData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucune destination" subtitle="Les destinations populaires apparaîtront ici" />
                                    ) : (
                                        <Box sx={{ height: 256 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={destinationPopularityData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" horizontal={false} />
                                                    <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} />
                                                    <YAxis type="category" dataKey="destination" width={90} tick={{ fontSize: 11, fill: C.navy }} />
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: C.navy, border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, fontSize: 12 }}
                                                        labelStyle={{ color: C.gold }}
                                                        itemStyle={{ color: C.bg }}
                                                        formatter={(value, name) => [
                                                            name === 'revenue' ? `${Number(value).toLocaleString('fr-FR')} €` : value,
                                                            name === 'revenue' ? 'Revenu' : 'Réservations',
                                                        ]}
                                                    />
                                                    <Bar dataKey="count" name="Réservations" fill="#c9a84c" radius={[0, 6, 6, 0]} maxBarSize={24} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )} */}
                                </SectionCard>
                            </Grid>

                            <Grid item xs={12} xl={6}>
                                <SectionCard title="Évolution des Dossiers par Mois" icon={<FolderOpen size={20} />}>
                                    {monthlyRevenueData.length === 0 ? (
                                        <EmptyState icon={<Inbox size={40} />} title="Aucune donnée" subtitle="L'évolution des dossiers apparaîtra ici" />
                                    ) : (
                                        <Box sx={{ height: 256 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={monthlyRevenueData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d8c8" vertical={false} />
                                                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.muted }} angle={-30} textAnchor="end" height={50} />
                                                    <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                                                    <Tooltip content={<ChartTooltip />} />
                                                    <Line type="monotone" dataKey="dossierCount" name="Dossiers" stroke="#c9a84c" strokeWidth={2.5}
                                                        dot={{ fill: '#c9a84c', r: 4 }} activeDot={{ r: 6 }} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </SectionCard>
                            </Grid>
                        </Grid>

                        {/* Activity Timeline */}
                        <SectionCard title="Chronologie des Activités Récentes" icon={<Activity size={20} />}>
                            {/* {activityTimelineData.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucune activité récente" subtitle="Les événements apparaîtront ici" />
                            ) : (
                                <Box sx={{ maxHeight: 384, overflowY: 'auto', pr: 0.5 }}>
                                    {activityTimelineData.map((event, idx) => {
                                        const config = activityTypeConfig[event.type] || activityTypeConfig['INVOICE_CREATED'];
                                        return (
                                            <motion.div key={`event-${idx}`}
                                                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                                            >
                                                <Stack
                                                    direction="row" spacing={1.5} alignItems="flex-start"
                                                    sx={{
                                                        py: 1.5,
                                                        borderBottom: idx < activityTimelineData.length - 1 ? `1px solid rgba(201,168,76,0.05)` : 'none',
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 32, height: 32, borderRadius: 2, flexShrink: 0, mt: 0.25,
                                                        bgcolor: `${config.color}12`, color: config.color,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        {config.icon}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
                                                            <Chip
                                                                label={config.label} size="small"
                                                                sx={{
                                                                    height: 16, fontSize: 9, fontWeight: 600, px: 0.5,
                                                                    bgcolor: `${config.color}18`, color: config.color,
                                                                }}
                                                            />
                                                            <Typography sx={{ fontSize: 10, color: C.dimmer, fontFamily: sans }}>
                                                                {safeDate(event.date, { day: 'numeric', month: 'short', year: 'numeric' })}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography sx={{ fontSize: 13, color: C.navy, fontFamily: sans }}>{event.description}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: C.faint, mt: 0.25, fontFamily: sans }}>par {event.user}</Typography>
                                                    </Box>
                                                </Stack>
                                            </motion.div>
                                        );
                                    })}
                                </Box>
                            )} */}
                        </SectionCard>
                    </motion.div>
                )}

                {/* ══════════════════════════════════════════════
                    CLIENTS & AGENTS
                ══════════════════════════════════════════════ */}
                {activeSection === 'clients' && (
                    <motion.div
                        key="clients"
                        role="tabpanel"
                        id="panel-clients"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                    >
                        {/* Top Clients table */}
                        <SectionCard title="Top Clients par Chiffre d'Affaires" icon={<Users size={20} />}>
                            {topClientsData.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucun client" subtitle="Le classement des clients apparaîtra ici" />
                            ) : (
                                <TableContainer sx={{ maxHeight: 480 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {['#', 'Client', 'CA', 'Dossiers', 'Dernière Activité'].map((h, i) => (
                                                    <TableCell key={h} align={i > 1 ? 'right' : 'left'} sx={{
                                                        fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8,
                                                        color: C.faint, fontWeight: 600, fontFamily: sans,
                                                        borderColor: C.borderLight, pb: 1.5,
                                                    }}>
                                                        {h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {topClientsData.map((client, idx) => (
                                                <TableRow
                                                    key={`client-${client.name || idx}`}
                                                    sx={{
                                                        '&:hover': { bgcolor: 'rgba(201,168,76,0.02)' },
                                                        transition: 'background 0.15s',
                                                    }}
                                                >
                                                    <TableCell sx={{ borderColor: 'rgba(201,168,76,0.05)', py: 1.5 }}>
                                                        <Box sx={{
                                                            width: 28, height: 28, borderRadius: '50%',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 12, fontWeight: 700,
                                                            bgcolor: idx < 3 ? 'rgba(201,168,76,0.15)' : 'rgba(26,26,46,0.06)',
                                                            color: idx < 3 ? C.gold : C.muted,
                                                        }}>
                                                            {idx + 1}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: 'rgba(201,168,76,0.05)', py: 1.5 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{
                                                                width: 32, height: 32, borderRadius: '50%',
                                                                bgcolor: 'rgba(201,168,76,0.1)', color: C.gold,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 12, fontWeight: 600,
                                                            }}>
                                                                {getInitials(client.name)}
                                                            </Box>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy, fontFamily: sans }}>
                                                                {client.name || 'Client Inconnu'}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ borderColor: 'rgba(201,168,76,0.05)', py: 1.5 }}>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                                            {(client.revenue || 0).toLocaleString('fr-FR')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ borderColor: 'rgba(201,168,76,0.05)', py: 1.5 }}>
                                                        <Typography sx={{ fontSize: 13, color: '#666', fontFamily: sans }}>{client.dossierCount || 0}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ borderColor: 'rgba(201,168,76,0.05)', py: 1.5 }}>
                                                        <Typography sx={{ fontSize: 12, color: C.faint, fontFamily: sans }}>
                                                            {safeDate(client.lastActivity, { day: 'numeric', month: 'short' })}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </SectionCard>

                        {/* Agent Performance */}
                        <SectionCard title="Performance des Agents" icon={<UserCheck size={20} />}>
                            {agentPerformanceData.length === 0 ? (
                                <EmptyState icon={<Inbox size={40} />} title="Aucun agent" subtitle="Les performances des agents apparaîtront ici" />
                            ) : (
                                <Stack spacing={2}>
                                    {agentPerformanceData.map((agent, idx) => (
                                        <motion.div key={`agent-${agent.name || idx}`}
                                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.1 }}
                                        >
                                            <Box sx={{
                                                border: `1px solid ${C.border}`, borderRadius: 3,
                                                p: 2.5, bgcolor: 'white',
                                            }}>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <Box sx={{
                                                        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                                                        background: `linear-gradient(135deg,${C.navy},${C.navyDark})`,
                                                        color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: 14, fontWeight: 700,
                                                    }}>
                                                        {getInitials(agent.name)}
                                                    </Box>

                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                            <Box>
                                                                <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                                                    {agent.name || 'Agent Inconnu'}
                                                                </Typography>
                                                                <Chip label={agent.role || 'ROLE'} size="small" sx={{
                                                                    height: 20, fontSize: 10, fontWeight: 600,
                                                                    bgcolor: 'rgba(201,168,76,0.1)', color: C.gold,
                                                                }} />
                                                            </Box>
                                                            <Box sx={{ textAlign: 'right' }}>
                                                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                                                    {(agent.totalRevenue || 0).toLocaleString('fr-FR')} FCFA
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: C.faint, fontFamily: sans }}>CA total</Typography>
                                                            </Box>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                                            {[
                                                                { label: 'Dossiers', value: agent.dossierCount || 0, color: C.navy, bg: 'rgba(201,168,76,0.05)' },
                                                                { label: 'Factures payées', value: agent.paidInvoices || 0, color: '#2e7d32', bg: 'rgba(46,125,50,0.05)' },
                                                                { label: 'En attente', value: agent.pendingInvoices || 0, color: '#ef6c00', bg: 'rgba(239,108,0,0.05)' },
                                                                { label: 'Panier moyen', value: `${(agent.avgDossierValue || 0).toLocaleString('fr-FR')} FCFA`, color: '#6a1b9a', bg: 'rgba(106,27,154,0.05)' },
                                                            ].map(({ label, value, color, bg }) => (
                                                                <Box key={label} sx={{ bgcolor: bg, borderRadius: 0.5, p: 1, textAlign: 'center', flex: 1, minWidth: 120 }}>
                                                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color, fontFamily: serif }}>{value}</Typography>
                                                                    <Typography sx={{ fontSize: 10, color: C.muted, fontFamily: sans }}>{label}</Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>

                                                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                            <Typography sx={{ fontSize: 12, color: '#666', fontFamily: sans }}>Taux de conversion</Typography>
                                                            <Typography sx={{
                                                                fontSize: 12, fontWeight: 700,
                                                                color: (agent.conversionRate || 0) >= 70 ? '#2e7d32' : '#ef6c00',
                                                            }}>
                                                                {agent.conversionRate || 0}%
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ height: 8, borderRadius: 2, bgcolor: C.pale, overflow: 'hidden' }}>
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${agent.conversionRate || 0}%` }}
                                                                transition={{ duration: 0.8, delay: 0.3 }}
                                                                style={{
                                                                    height: '100%', borderRadius: 8,
                                                                    background: (agent.conversionRate || 0) >= 70
                                                                        ? 'linear-gradient(135deg,#1b5e20,#2e7d32)'
                                                                        : 'linear-gradient(135deg,#e65100,#ef6c00)',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </Stack>
                            )}
                        </SectionCard>

                        {/* Client Retention + New Clients */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
                                    <Card sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
                                            bgcolor: 'rgba(46,125,50,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Percent size={28} color="#2e7d32" />
                                        </Box>
                                        <Typography sx={{ fontSize: 32, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                            {kpi.clientRetentionRate || 0}%
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, color: '#666', mt: 0.5, fontFamily: sans }}>Taux de Rétention Client</Typography>
                                        <Typography sx={{ fontSize: 12, color: C.faint, mt: 1, fontFamily: sans }}>
                                            {kpi.clientCount || 0} clients actifs sur le portefeuille
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                                    <Card sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
                                            bgcolor: 'rgba(201,168,76,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Users size={28} color={C.gold} />
                                        </Box>
                                        <Typography sx={{ fontSize: 32, fontWeight: 700, color: C.navy, fontFamily: serif }}>
                                            {kpi.newClientsThisMonth || 0}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, color: '#666', mt: 0.5, fontFamily: sans }}>Nouveaux Clients ce Mois</Typography>
                                        <Typography sx={{ fontSize: 12, color: C.faint, mt: 1, fontFamily: sans }}>
                                            Panier moyen : {(kpi.avgInvoiceValue || 0).toLocaleString('fr-FR')}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        </Grid>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

export default ReportingModule;